import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  FileText,
  Pencil,
  Eye,
  Search,
  Users,
  DollarSign,
  TrendingDown,
  Briefcase,
  CheckSquare,
  SlidersHorizontal as Filters,
  X,
  ChevronDown,
  ChevronUp,
  LayoutDashboard,
  RefreshCw,
  Download
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../../api/axios';
import styles from './Payroll.module.css';
import Pagination from '../../../components/Common/Pagination/Pagination';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import { useLoading } from '../../../context/LoadingContext';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';

const monthNames = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const statusOptions = [
  { value: 'draft', label: 'Draft' },
  { value: 'generated', label: 'Generated' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' }
];

const MonthlyPayrollView = () => {
  const { year, month } = useParams();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoading();
  const mainContentRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [payrolls, setPayrolls] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [isBulkPaying, setIsBulkPaying] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [approvingId, setApprovingId] = useState(null);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0,
    per_page: 50,
  });

  const monthName = monthNames[parseInt(month)] || '';

  const fetchPayrolls = useCallback(async (page = 1, isPartial = false) => {
    if (isPartial) {
      setIsRefreshing(true);
    } else {
      showLoader();
    }
    try {
      const params = {
        month,
        year,
        page,
        per_page: pagination.per_page
      };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;

      const response = await api.get('/payroll', { params, skipLoader: isPartial });
      if (response.data.success) {
        setPayrolls(response.data.data.data);
        setPagination({
          current_page: response.data.data.current_page,
          last_page: response.data.data.last_page,
          total: response.data.data.total,
          per_page: response.data.data.per_page,
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch payrolls');
    } finally {
      if (isPartial) {
        setIsRefreshing(false);
      } else {
        hideLoader();
      }
    }
  }, [month, year, search, statusFilter, pagination.per_page, showLoader, hideLoader]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await api.get('/payroll/summary', { params: { month, year } });
      if (response.data.success) {
        setSummary(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch summary', error);
    }
  }, [month, year]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchPayrolls(1);
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [search, statusFilter, fetchPayrolls]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.last_page) {
      fetchPayrolls(page);
    }
  };

  const formatCurrency = (amount) => {
    const val = parseFloat(amount) || 0;
    const display = val < 0 ? 0 : val;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(display);
  };

  const formatCurrencyRaw = (amount) => {
    const val = parseFloat(amount) || 0;
    const display = val < 0 ? 0 : val;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(display);
  };

  const handleApprove = async (payrollId) => {
    setApprovingId(payrollId);
    try {
      const response = await api.post(`/payroll/${payrollId}/approve`, {});
      if (response.data.success) {
        toast.success('Payroll approved successfully');
        fetchPayrolls(pagination.current_page);
        fetchSummary();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to approve payroll');
    } finally {
      setApprovingId(null);
    }
  };

  const handleBulkApprove = async () => {
    setBulkApproving(true);
    try {
      const response = await api.post('/payroll/bulk-approve', { month: parseInt(month), year: parseInt(year) });
      if (response.data.success) {
        toast.success(response.data.message);
        fetchPayrolls(pagination.current_page);
        fetchSummary();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to bulk approve');
    } finally {
      setBulkApproving(false);
    }
  };

  const handleExportBankStatement = async () => {
    setIsExporting(true);
    try {
      const response = await api.get('/payroll/export-bank-statement', {
        params: { month: parseInt(month), year: parseInt(year) },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Bank_Statement_${monthName}_${year}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export successful');
    } catch (error) {
      toast.error('Failed to export bank statement');
    } finally {
      setIsExporting(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'EM';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const hasGeneratedPayrolls = payrolls.some(p => p.status === 'generated');

  return (
    <div className={styles['payroll-container']}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']} ref={mainContentRef}>
          <Breadcrumbs customItems={[
            { label: 'HR', path: '#' },
            { label: 'Payroll / Salary', path: '/employees/payroll' },
            { label: `${monthName} ${year}`, path: '#' }
          ]} />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <button
                onClick={() => navigate('/employees/payroll')}
                className={styles['back-link']}
              >
                <ArrowLeft size={18} /> Back to Payroll
              </button>
              <h1>{monthName} {year} — Payroll</h1>
              <p>Detailed employee payroll breakdown for {monthName} {year}</p>
            </div>
            <div className={styles['header-actions']}>
              <button
                onClick={handleExportBankStatement}
                className={styles['btn-secondary']}
                disabled={isExporting}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Download size={18} />
                <span>{isExporting ? 'Exporting...' : 'Export Bank CSV'}</span>
              </button>
              {hasGeneratedPayrolls && (
                <button
                  onClick={handleBulkApprove}
                  className={styles['btn-approve-all']}
                  disabled={bulkApproving}
                >
                  <CheckSquare size={18} />
                  <span>{bulkApproving ? 'Approving...' : 'Approve All'}</span>
                </button>
              )}
            </div>
          </div>

          {/* Summary Cards */}
          {summary && (
            <div className={styles['summary-collapsible']}>
              <button
                className={styles['summary-toggle']}
                onClick={() => setIsSummaryOpen(prev => !prev)}
              >
                <div className={styles['summary-toggle-left']}>
                  <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                  <div className={styles['summary-toggle-text']}>
                    <span className={styles['summary-toggle-title']}>Summary</span>
                    <span className={styles['summary-toggle-hint']}>
                      {summary.total_payrolls} Payrolls · {formatCurrencyRaw(summary.total_net_salary)} Net
                    </span>
                  </div>
                </div>
                {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>

              <div className={`${styles['summary-metrics-row']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
                <div className={`${styles['metric-card']} ${styles['purple']}`}>
                  <div className={styles['metric-icon-box']}>
                    <Users size={24} />
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>Total Payrolls</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{summary.total_payrolls}</span>
                    </div>
                  </div>
                </div>

                <div className={`${styles['metric-card']} ${styles['green']}`}>
                  <div className={styles['metric-icon-box']}>
                    <DollarSign size={24} />
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>Net Salary Payout</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{formatCurrencyRaw(summary.total_net_salary)}</span>
                    </div>
                  </div>
                </div>

                <div className={`${styles['metric-card']} ${styles['orange']} ${styles['highlight-card']}`}>
                  <div className={styles['metric-icon-box']}>
                    <TrendingDown size={24} />
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>Total Deductions</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{formatCurrencyRaw(summary.total_deductions)}</span>
                    </div>
                  </div>
                  <div className={styles['glow-effect']}></div>
                </div>

                <div className={`${styles['metric-card']} ${styles['blue']}`}>
                  <div className={styles['metric-icon-box']}>
                    <Briefcase size={24} />
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>Gross Salary</span>
                    <div className={styles['metric-value-group']}>
                      <span className={styles['metric-value']}>{formatCurrencyRaw(summary.total_gross_salary)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Table Controls */}
          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search Employee Name or Code..."
                className={styles['search-input']}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: pagination.per_page, label: `${pagination.per_page} per page` }}
                  onChange={(option) => {
                    setPagination(prev => ({ ...prev, per_page: option.value }));
                    fetchPayrolls(1);
                  }}
                  options={[
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>

              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {statusFilter && <span className={styles['filter-badge']}></span>}
              </button>

              <button
                className={styles['filter-btn']}
                onClick={() => {
                  fetchPayrolls(pagination.current_page, true);
                  fetchSummary();
                }}
                disabled={isRefreshing}
                title="Refresh Table Data"
              >
                <RefreshCw size={18} className={isRefreshing ? 'spin' : ''} />
                <span>{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Payrolls</h3>
                  <p>Filter by status.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Status</label>
                  <CustomSelect
                    value={statusOptions.find(s => s.value === statusFilter) || null}
                    onChange={(opt) => setStatusFilter(opt ? opt.value : '')}
                    options={statusOptions}
                    placeholder="All Statuses"
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['btn-reset']} onClick={() => setStatusFilter('')}>Reset</button>
                <button className={styles['btn-apply']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          {/* Employee Payroll Desktop Table */}
          <div className={styles['custom-table-container']} style={{ opacity: isRefreshing ? 0.6 : 1, transition: 'opacity 0.2s ease-in-out' }}>
            <div className={styles['custom-table-wrapper']}>
              <table className={styles['custom-table']}>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Total Days</th>
                    <th>Present</th>
                    <th>Absent</th>
                    <th>OT Days</th>
                    <th>Loan EMI</th>
                    <th>Advance</th>
                    <th>PF</th>
                    <th>ESIC</th>
                    <th>Prof Tax</th>
                    <th>Other Ded.</th>
                    <th>Gross</th>
                    <th>Net Salary</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payrolls.length === 0 ? (
                    <tr>
                      <td colSpan="15" className={styles.noData}>
                        No payrolls found for {monthName} {year}.
                      </td>
                    </tr>
                  ) : (
                    payrolls.map(payroll => (
                      <tr key={payroll.id} className={styles[`row-${payroll.status}`]}>
                        <td>
                          <div className={styles['employee-cell']}>
                            <div className={styles['employee-avatar-sm']}>
                              {getInitials(payroll.employee?.employee_name)}
                            </div>
                            <div className={styles['employee-info-cell']}>
                              <span className={styles['employee-name-cell']}>{payroll.employee?.employee_name}</span>
                              <span className={styles['employee-code-cell']}>{payroll.employee?.employee_code}</span>
                            </div>
                          </div>
                        </td>
                        <td>{payroll.total_days_in_month}</td>
                        <td>
                          <span className={styles['text-success']}>{payroll.present_days}</span>
                        </td>
                        <td>
                          <span className={parseFloat(payroll.absent_days) > 0 ? styles['text-danger'] : ''}>
                            {payroll.absent_days}
                          </span>
                        </td>
                        <td>{payroll.ot_days > 0 ? payroll.ot_days : '—'}</td>
                        <td>{formatCurrency(payroll.loan_emi_deduction)}</td>
                        <td>{formatCurrency(payroll.advance_deduction)}</td>
                        <td>{formatCurrency(payroll.pf_deduction)}</td>
                        <td>{formatCurrency(payroll.esic_deduction)}</td>
                        <td>{formatCurrency(payroll.professional_tax)}</td>
                        <td>{formatCurrency(payroll.other_deduction)}</td>
                        <td>
                          <span className={styles['text-bold']}>{formatCurrency(payroll.gross_salary)}</span>
                        </td>
                        <td>
                          <span className={`${styles['text-bold']} ${parseFloat(payroll.net_salary) < 0 ? styles['text-zero'] : styles['text-success']}`}>
                            {formatCurrency(payroll.net_salary)}
                          </span>
                        </td>
                        <td>
                          <span className={`${styles.statusBadge} ${styles[`status${payroll.status.charAt(0).toUpperCase() + payroll.status.slice(1)}`]}`}>
                            {payroll.status}
                          </span>
                        </td>
                        <td>
                          <div className={styles['action-btn-group']}>
                            <button
                              className={styles['action-icon-btn']}
                              onClick={() => navigate(`/employees/payroll/view/${payroll.id}`)}
                              title="View Payslip"
                            >
                              <Eye size={16} />
                            </button>
                            {(payroll.status === 'draft' || payroll.status === 'generated') && (
                              <button
                                className={`${styles['action-icon-btn']} ${styles['action-edit']}`}
                                onClick={() => navigate(`/employees/payroll/edit/${payroll.id}`)}
                                title="Edit Payroll"
                              >
                                <Pencil size={16} />
                              </button>
                            )}
                            {payroll.status === 'generated' && (
                              <button
                                className={`${styles['action-icon-btn']} ${styles['action-approve']}`}
                                onClick={() => handleApprove(payroll.id)}
                                disabled={approvingId === payroll.id}
                                title="Approve Payroll"
                              >
                                <CheckCircle size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards View */}
            <div className={styles['payroll-cards']}>
              {payrolls.length === 0 ? (
                <div className={styles.noData}>No payrolls found for {monthName} {year}.</div>
              ) : (
                payrolls.map(payroll => (
                  <div key={payroll.id} className={styles['payroll-card']}>
                    <div className={styles['card-main']}>
                      <div className={styles['employee-cell']}>
                        <div className={styles['employee-avatar-sm']}>
                          {getInitials(payroll.employee?.employee_name)}
                        </div>
                        <div className={styles['employee-info-cell']}>
                          <span className={styles['employee-name-cell']}>{payroll.employee?.employee_name}</span>
                          <span className={styles['employee-code-cell']}>{payroll.employee?.employee_code}</span>
                        </div>
                      </div>
                      <span className={`${styles.statusBadge} ${styles[`status${payroll.status.charAt(0).toUpperCase() + payroll.status.slice(1)}`]}`}>
                        {payroll.status}
                      </span>
                    </div>

                    <div className={styles['card-details']}>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Gross</span>
                        <span className={styles['detail-value']}>{formatCurrency(payroll.gross_salary)}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Net Salary</span>
                        <span className={`${styles['detail-value']} ${parseFloat(payroll.net_salary) < 0 ? styles['has-balance'] : styles['text-success']}`}>{formatCurrency(payroll.net_salary)}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Present / Total</span>
                        <span className={styles['detail-value']}>{payroll.present_days} / {payroll.total_days_in_month}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Total Deductions</span>
                        <span className={`${styles['detail-value']} ${styles['has-balance']}`}>
                          {formatCurrency(
                            parseFloat(payroll.loan_emi_deduction || 0) +
                            parseFloat(payroll.advance_deduction || 0) +
                            parseFloat(payroll.pf_deduction || 0) +
                            parseFloat(payroll.esic_deduction || 0) +
                            parseFloat(payroll.professional_tax || 0) +
                            parseFloat(payroll.other_deduction || 0)
                          )}
                        </span>
                      </div>
                    </div>

                    <div className={styles['card-footer']}>
                      <div className={styles['action-btn-group']}>
                        <button
                          className={styles['action-icon-btn']}
                          onClick={() => navigate(`/employees/payroll/view/${payroll.id}`)}
                          title="View Payslip"
                        >
                          <Eye size={16} />
                        </button>
                        {(payroll.status === 'draft' || payroll.status === 'generated') && (
                          <button
                            className={`${styles['action-icon-btn']} ${styles['action-edit']}`}
                            onClick={() => navigate(`/employees/payroll/edit/${payroll.id}`)}
                            title="Edit Payroll"
                          >
                            <FileText size={16} />
                          </button>
                        )}
                        {payroll.status === 'generated' && (
                          <button
                            className={`${styles['action-icon-btn']} ${styles['action-approve']}`}
                            onClick={() => handleApprove(payroll.id)}
                            disabled={approvingId === payroll.id}
                            title="Approve Payroll"
                          >
                            <CheckCircle size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {pagination.total > 0 && (
              <div className={styles['desktop-pagination']}>
                <div className={styles['pagination-container']}>
                  <Pagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.last_page}
                    onPageChange={handlePageChange}
                    totalItems={pagination.total}
                    itemsPerPage={pagination.per_page}
                  />
                </div>
              </div>
            )}
          </div>

          {pagination.total > 0 && (
            <div className={styles['mobile-pagination']}>
              <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.last_page}
                onPageChange={handlePageChange}
                totalItems={pagination.total}
                itemsPerPage={pagination.per_page}
              />
            </div>
          )}

        </main>

        <Footer />
      </div>
    </div>
  );
};

export default MonthlyPayrollView;

